//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React, { ReactChild } from 'react';

import { BodyBoldText } from '@components/atoms/text/Text';
import IItemMetadata from '@models/IItemMetadata';
import { isDefined } from '@utils/filterPredicates';
import { readableNumber, readableRate } from '@utils/readableNumber';

enum StatName {
  COMMENTS = 'comments',
  ENGAGEMENT_RATE = 'engagementRate',
  FOLLOWER_ENGAGEMENT_RATE = 'followerEngagementRate',
  LIKES = 'likes',
  REACH = 'reach',
  REACH_ENGAGEMENT_RATE = 'reachEngagementRate',
  REPINS = 'repins',
}

const statIcons = {
  [StatName.COMMENTS]: 'comments',
  [StatName.ENGAGEMENT_RATE]: 'multiple-users',
  [StatName.FOLLOWER_ENGAGEMENT_RATE]: 'multiple-users',
  [StatName.LIKES]: 'likes',
  [StatName.REACH]: 'reach',
  [StatName.REACH_ENGAGEMENT_RATE]: 'bar-graph',
  [StatName.REPINS]: 'pinterest',
} as const;

const statTooltips = {
  [StatName.COMMENTS]: (
    <>
      <BodyBoldText>Comments:</BodyBoldText>
      Number of comments this post received.
    </>
  ),
  [StatName.ENGAGEMENT_RATE]: (
    <>
      <BodyBoldText>Follower Engagement Rate:</BodyBoldText>
      The number of likes plus the number of comments divided by the number of
      current followers.
    </>
  ),
  [StatName.FOLLOWER_ENGAGEMENT_RATE]: (
    <>
      <BodyBoldText>Follower Engagement Rate:</BodyBoldText>
      The number of likes plus the number of comments divided by the number of
      current followers.
    </>
  ),
  [StatName.LIKES]: (
    <>
      <BodyBoldText>Likes:</BodyBoldText>
      Number of times this post was liked.
    </>
  ),
  [StatName.REACH]: (
    <>
      <BodyBoldText>Reach:</BodyBoldText>
      The number of unique users who have seen this media.
    </>
  ),
  [StatName.REACH_ENGAGEMENT_RATE]: (
    <>
      <BodyBoldText>Engagement Rate:</BodyBoldText>
      The number of likes plus the number of comments divided by the number of
      unique users who have seen this media.
    </>
  ),
  [StatName.REPINS]: (
    <>
      <BodyBoldText>Repins:</BodyBoldText>
      The number of saves this post received.
    </>
  ),
} as const;

export interface ItemStats {
  name: StatName;
  value: string;
  tooltip: ReactChild;
  icon: UnisonIconType;
}

const percentageConfig = { rateIncludePercentSuffix: true, rateDecimals: 2 };

const toItemStat = (
  name: StatName,
  value: number | undefined,
): ItemStats | undefined => {
  if (!isDefined(value)) {
    return undefined;
  }

  switch (name) {
    case StatName.LIKES: {
      return {
        name,
        value: readableNumber(value),
        tooltip: statTooltips[name],
        icon: statIcons[name],
      };
    }
    case StatName.COMMENTS: {
      return {
        name,
        value: readableNumber(value),
        tooltip: statTooltips[name],
        icon: statIcons[name],
      };
    }
    case StatName.FOLLOWER_ENGAGEMENT_RATE: {
      return {
        name,
        value: readableRate(value * 100, percentageConfig),
        tooltip: statTooltips[name],
        icon: statIcons[name],
      };
    }
    case StatName.REACH_ENGAGEMENT_RATE: {
      return {
        name,
        value: readableRate(value * 100, percentageConfig),
        tooltip: statTooltips[name],
        icon: statIcons[name],
      };
    }
    case StatName.ENGAGEMENT_RATE: {
      return {
        name,
        value: readableRate(value * 100, percentageConfig),
        tooltip: statTooltips[name],
        icon: statIcons[name],
      };
    }
    case StatName.REACH: {
      return {
        name,
        value: readableNumber(value),
        tooltip: statTooltips[name],
        icon: statIcons[name],
      };
    }
    case StatName.REPINS: {
      return {
        name,
        value: readableNumber(value),
        tooltip: statTooltips[name],
        icon: statIcons[name],
      };
    }
  }
};

export const getItemStats = (metadata: IItemMetadata | undefined) => {
  if (isDefined(metadata)) {
    return [
      toItemStat(StatName.LIKES, metadata.socialDetails.likeCount),
      toItemStat(StatName.COMMENTS, metadata.socialDetails.commentCount),
      toItemStat(
        StatName.FOLLOWER_ENGAGEMENT_RATE,
        metadata.socialDetails.followerEngagementRate,
      ),
      toItemStat(
        StatName.REACH_ENGAGEMENT_RATE,
        metadata.socialDetails.instagramDetails?.reachEngagementRate,
      ),
      toItemStat(
        StatName.REACH,
        metadata.socialDetails.instagramDetails?.reach,
      ),
      toItemStat(StatName.REPINS, metadata.socialDetails.repinCount),
    ].filter((stats) => isDefined(stats));
  } else {
    return [];
  }
};
